import React, {useCallback, useEffect, useMemo, useState} from "react"
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "@mui/material"
import {LineChart} from "@mui/x-charts/LineChart"
import {DatePicker} from "@mui/x-date-pickers/DatePicker"
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider"
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs"
import {Dayjs} from "dayjs"

import {api, Environment, MetricQueryParams, MonthlyMetric} from "@indebted/api"
import {Layout} from "@indebted/components/Layout"

// Currency to color mapping
const currencyColorMap: Record<string, string> = {
	AUD: "#0072B2", // Blue
	CAD: "#E69F00", // Orange
	GBP: "#009E73", // Bluish Green
	NZD: "#56B4E9", // Sky Blue
	USD: "#2B2B2B", // Dark Gray / Near Black
	MXN: "#CC79A7", // Reddish Purple
	AED: "#F0E442", // Yellow
	BRL: "#D55E00", // Vermillion
}

const getCurrencyColor = (currency: string): string => currencyColorMap[currency]

const formatNumber = (value: number | null) => {
	if (value === null) return ""
	if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
	if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
	return value.toFixed(0)
}

const formatDate = (dateString: string, period?: string) => {
	const date = new Date(dateString)

	if (period === "daily") {
		return date.toLocaleDateString(undefined, {year: "numeric", month: "short", day: "numeric"})
	}

	if (period === "weekly") {
		return date.toLocaleDateString(undefined, {year: "numeric", month: "short", day: "numeric"})
	}

	// Default to monthly format
	return date.toLocaleDateString(undefined, {year: "numeric", month: "short"})
}

type MetricsState = {
	commissionMetrics: MonthlyMetric[]
	paymentMetrics: MonthlyMetric[]
	customerMetrics: MonthlyMetric[]
	stripeFeesMetrics: MonthlyMetric[]
	loading: boolean
	error: string
	selectedCurrencies: Set<string>
	environment: Environment
	filters: MetricQueryParams
	commissionType: "net" | "gross"
}

type ChartData = {
	months: string[]
	series: Array<{
		id: string
		label: string
		data: (number | null)[]
		connectNulls: boolean
		color: string
	}>
}

// Reusable ChartCard component
const ChartCard = ({
	title,
	chartData,
	onLegendClick,
	width = "100%",
}: {
	title: string
	chartData: ChartData
	onLegendClick: (currency: string) => void
	width?: string
}) => (
	<Box width={width}>
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					{title}
				</Typography>
				<LineChart
					xAxis={[{scaleType: "point", data: chartData.months}]}
					yAxis={[{valueFormatter: formatNumber}]}
					series={chartData.series}
					height={400}
					slotProps={{
						legend: {
							onItemClick: (event, context) => {
								event.preventDefault()
								onLegendClick(context.seriesId as string)
							},
						},
					}}
				/>
			</CardContent>
		</Card>
	</Box>
)

function MetricsPage() {
	const [state, setState] = useState<MetricsState>({
		commissionMetrics: [],
		paymentMetrics: [],
		customerMetrics: [],
		stripeFeesMetrics: [],
		loading: true,
		error: "",
		selectedCurrencies: new Set(),
		environment: "prod",
		filters: {},
		commissionType: "net",
	})

	const [tempFilters, setTempFilters] = useState<MetricQueryParams>({})
	const [startDate, setStartDate] = useState<Dayjs | null>(null)
	const [endDate, setEndDate] = useState<Dayjs | null>(null)

	const loadMetrics = useCallback((env: Environment, filters: MetricQueryParams) => {
		setState((prev) => ({...prev, loading: true, error: ""}))
		api.setEnvironment(env)

		Promise.all([
			api.convertMetrics.getMonthlyCommissionAmount(filters),
			api.convertMetrics.getMonthlyPaymentAmount(filters),
			api.convertMetrics.getMonthlyUniqueCustomer(filters),
			api.convertMetrics.getMonthlyStripeFees(filters),
		])
			.then(([commission, payment, customer, stripeFees]) => {
				setState((prev) => ({
					...prev,
					commissionMetrics: commission,
					paymentMetrics: payment,
					customerMetrics: customer,
					stripeFeesMetrics: stripeFees,
					loading: false,
					environment: env,
					filters,
				}))
			})
			.catch((error) => {
				setState((prev) => ({
					...prev,
					loading: false,
					error: error.Message || "Failed to load metrics",
					environment: env,
				}))
			})
	}, [])

	useEffect(() => {
		loadMetrics("prod", {})
	}, [loadMetrics])

	const handleEnvironmentChange = (_event: React.MouseEvent<HTMLElement>, newEnvironment: Environment | null) => {
		if (newEnvironment !== null) loadMetrics(newEnvironment, state.filters)
	}

	const handleCommissionTypeChange = (
		_event: React.MouseEvent<HTMLElement>,
		newCommissionType: "net" | "gross" | null,
	) => {
		if (newCommissionType !== null) {
			setState((prev) => ({...prev, commissionType: newCommissionType}))
		}
	}

	const handleUpdateFilters = () => {
		const filters: MetricQueryParams = {
			...tempFilters,
			start: startDate ? startDate.format("YYYY-MM-DD") : undefined,
			end: endDate ? endDate.format("YYYY-MM-DD") : undefined,
		}
		loadMetrics(state.environment, filters)
	}

	const toggleCurrency = useCallback((currency: string) => {
		setState((prev) => {
			const newSelected = new Set(prev.selectedCurrencies)
			if (newSelected.has(currency)) {
				newSelected.delete(currency)
			} else {
				newSelected.add(currency)
			}
			return {...prev, selectedCurrencies: newSelected}
		})
	}, [])

	const allCurrencies = useMemo(
		() =>
			Array.from(
				new Set([
					...state.commissionMetrics.map((m) => m.Currency),
					...state.paymentMetrics.map((m) => m.Currency),
					...state.customerMetrics.map((m) => m.Currency),
					...state.stripeFeesMetrics.map((m) => m.Currency),
				]),
			),
		[state.commissionMetrics, state.paymentMetrics, state.customerMetrics, state.stripeFeesMetrics],
	)

	const activeCurrencies = useMemo(
		() => (state.selectedCurrencies.size === 0 ? allCurrencies : Array.from(state.selectedCurrencies)),
		[allCurrencies, state.selectedCurrencies],
	)

	// Generic chart data builder
	const buildChartData = useCallback(
		(metrics: MonthlyMetric[], valueTransform: (value: number) => number): ChartData => {
			const period = state.filters.period
			const dates = Array.from(new Set(metrics.map((m) => formatDate(m.Month, period)))).sort(
				(a, b) => new Date(a).getTime() - new Date(b).getTime(),
			)

			const currencies = Array.from(new Set(metrics.map((m) => m.Currency)))

			const series = currencies.map((currency) => {
				const isActive = activeCurrencies.includes(currency)
				const data = dates.map((date) => {
					if (!isActive) return null
					const metric = metrics.find((m) => formatDate(m.Month, period) === date && m.Currency === currency)
					return metric ? valueTransform(metric.Value) : null
				})

				return {
					id: currency,
					label: currency,
					data,
					connectNulls: true,
					color: isActive ? getCurrencyColor(currency) : "#bdbdbd",
				}
			})

			return {months: dates, series}
		},
		[activeCurrencies, state.filters.period],
	)

	const commissionChartData = useMemo(
		() => buildChartData(state.commissionMetrics, (v) => v / 100),
		[state.commissionMetrics, buildChartData],
	)

	const customerChartData = useMemo(
		() => buildChartData(state.customerMetrics, (v) => v),
		[state.customerMetrics, buildChartData],
	)

	const stripeFeesChartData = useMemo(
		() => buildChartData(state.stripeFeesMetrics, (v) => Math.abs(v) / 100),
		[state.stripeFeesMetrics, buildChartData],
	)

	const paymentChartData = useMemo(
		() => buildChartData(state.paymentMetrics, (v) => v / 100),
		[state.paymentMetrics, buildChartData],
	)

	const averageCommissionData = useMemo(() => {
		const period = state.filters.period
		const dates = Array.from(new Set(state.commissionMetrics.map((m) => formatDate(m.Month, period)))).sort(
			(a, b) => new Date(a).getTime() - new Date(b).getTime(),
		)

		const currencies = Array.from(new Set(state.commissionMetrics.map((m) => m.Currency)))

		const series = currencies.map((currency) => {
			const isActive = activeCurrencies.includes(currency)
			const data = dates.map((date) => {
				if (!isActive) return null

				const findMetric = (metrics: MonthlyMetric[]) =>
					metrics.find((m) => formatDate(m.Month, period) === date && m.Currency === currency)

				const commission = findMetric(state.commissionMetrics)
				const stripeFees = findMetric(state.stripeFeesMetrics)
				const customers = findMetric(state.customerMetrics)

				if (!commission || !customers || customers.Value <= 0) return null

				let commissionValue: number
				if (state.commissionType === "net") {
					const stripeFeeValue = stripeFees ? stripeFees.Value : 0
					const netCommissionInCents = commission.Value - Math.abs(stripeFeeValue)
					commissionValue = netCommissionInCents / 100
				} else {
					// gross commission (before stripe fees)
					commissionValue = commission.Value / 100
				}

				return commissionValue / customers.Value
			})

			return {
				id: currency,
				label: currency,
				data,
				connectNulls: true,
				color: isActive ? getCurrencyColor(currency) : "#bdbdbd",
			}
		})

		return {months: dates, series}
	}, [
		state.commissionMetrics,
		state.customerMetrics,
		state.stripeFeesMetrics,
		activeCurrencies,
		state.filters.period,
		state.commissionType,
	])

	if (state.loading) {
		return (
			<Layout>
				<Box display="flex" justifyContent="center" alignItems="center" height="100%">
					<CircularProgress />
				</Box>
			</Layout>
		)
	}

	if (state.error) {
		return (
			<Layout>
				<Box p={3}>
					<Alert severity="error">{state.error}</Alert>
				</Box>
			</Layout>
		)
	}

	return (
		<Layout>
			<Box p={3}>
				<Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
					<Typography variant="h4">Convert Metrics Dashboard</Typography>
					<ToggleButtonGroup
						value={state.environment}
						exclusive
						onChange={handleEnvironmentChange}
						aria-label="environment selector"
						size="small"
					>
						<ToggleButton value="dev" aria-label="dev environment">
							DEV
						</ToggleButton>
						<ToggleButton value="prod" aria-label="prod environment">
							PROD
						</ToggleButton>
					</ToggleButtonGroup>
				</Box>

				<Card sx={{mb: 3}}>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							Filters
						</Typography>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
								<DatePicker
									label="Start Date"
									value={startDate}
									onChange={(newValue) => setStartDate(newValue)}
									slotProps={{
										textField: {
											size: "small",
											sx: {minWidth: 180},
										},
									}}
								/>
								<DatePicker
									label="End Date"
									value={endDate}
									onChange={(newValue) => setEndDate(newValue)}
									slotProps={{
										textField: {
											size: "small",
											sx: {minWidth: 180},
										},
									}}
								/>
								<FormControl size="small" sx={{minWidth: 150}}>
									<InputLabel>Period</InputLabel>
									<Select
										value={tempFilters.period || ""}
										onChange={(e) =>
											setTempFilters((prev) => ({...prev, period: e.target.value || undefined}))
										}
										label="Period"
									>
										<MenuItem value="">
											<em>(default)</em>
										</MenuItem>
										<MenuItem value="daily">Daily</MenuItem>
										<MenuItem value="weekly">Weekly</MenuItem>
										<MenuItem value="monthly">Monthly</MenuItem>
									</Select>
								</FormControl>
								<Button variant="contained" onClick={handleUpdateFilters} disabled={state.loading}>
									UPDATE
								</Button>
							</Box>
						</LocalizationProvider>
					</CardContent>
				</Card>

				<Box display="flex" flexDirection="column" gap={3}>
					{state.commissionMetrics.length > 0 && state.customerMetrics.length > 0 && (
						<Box>
							<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
								<Typography variant="h6" sx={{ml: 2, mt: 1}}>
									Average {state.commissionType === "net" ? "Net" : "Gross"} Commission Per Customer
								</Typography>
								<ToggleButtonGroup
									value={state.commissionType}
									exclusive
									onChange={handleCommissionTypeChange}
									aria-label="commission type selector"
									size="small"
									sx={{mr: 2, mt: 1}}
								>
									<ToggleButton value="net" aria-label="net commission">
										Net
									</ToggleButton>
									<ToggleButton value="gross" aria-label="gross commission">
										Gross
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
							<ChartCard title="" chartData={averageCommissionData} onLegendClick={toggleCurrency} />
						</Box>
					)}

					<Box display="flex" gap={3}>
						{state.commissionMetrics.length > 0 && (
							<ChartCard
								title="Commission Amount"
								chartData={commissionChartData}
								onLegendClick={toggleCurrency}
								width="49%"
							/>
						)}

						{state.customerMetrics.length > 0 && (
							<ChartCard
								title="Unique Customers"
								chartData={customerChartData}
								onLegendClick={toggleCurrency}
								width="49%"
							/>
						)}
					</Box>

					<Box display="flex" gap={3}>
						{state.paymentMetrics.length > 0 && (
							<ChartCard
								title="Payment Amount"
								chartData={paymentChartData}
								onLegendClick={toggleCurrency}
								width="49%"
							/>
						)}

						{state.stripeFeesMetrics.length > 0 && (
							<ChartCard
								title="Stripe Fees"
								chartData={stripeFeesChartData}
								onLegendClick={toggleCurrency}
								width="49%"
							/>
						)}
					</Box>
				</Box>
			</Box>
		</Layout>
	)
}

export {MetricsPage}
